import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import SearchBox from '@adsk/bim360-matrix-react-search-box';
import InputValidationView from '@adsk/bim360-matrix-react-input-validation-view';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';

import './AssetsPickerSearchBox.scss';

const SEARCH_EXPRESSION_MIN_LENGTH = 3;
const SEARCH_EXPRESSION_MAX_LENGTH = 100;
const SHOW_ERROR_DELAY = 500;

const AssetsPickerSearchBox = ({ onSearchChange, disabled }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState();
  const showErrorTimeoutId = useRef();
  const { T } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
  const searchPlaceholder = T.translate('SEARCH_PLACEHOLDER');

  const validateSearchTerm = useCallback(searchTerm => {
    if (!searchTerm.length) return undefined;

    if (searchTerm.length < SEARCH_EXPRESSION_MIN_LENGTH)
      return T.translate('SEARCH_EXPRESSION_TOO_SHORT', {
        minStringLength: SEARCH_EXPRESSION_MIN_LENGTH
      });

    if (searchTerm.length > SEARCH_EXPRESSION_MAX_LENGTH)
      return T.translate('SEARCH_EXPRESSION_TOO_LONG', {
        maxStringLength: SEARCH_EXPRESSION_MAX_LENGTH
      });

    return undefined;
  }, [T]);

  const onChange = useCallback(
    searchText => {
      const newErrorMessage = validateSearchTerm(searchText);

      setSearchTerm(searchText);
      onSearchChange(newErrorMessage ? '' : searchText);

      clearTimeout(showErrorTimeoutId.current);

      if (!errorMessage && newErrorMessage) {
        showErrorTimeoutId.current = window.setTimeout(() => {
          setErrorMessage(newErrorMessage);
        }, SHOW_ERROR_DELAY);
      } else if (errorMessage !== newErrorMessage) {
        setErrorMessage(newErrorMessage);
      }
    },
    [validateSearchTerm, onSearchChange, errorMessage]
  );

  useEffect(
    // effect with just a clean up (note the double arrow fn)
    // to prevent the setTimeout callback to update the
    // errorMessage state after the component unmounted
    () => () => {
      clearTimeout(showErrorTimeoutId.current);
    },
    []
  );

  return (
    <div className="AssetsPickerSearchBox">
      <InputValidationView message={errorMessage}>
        <SearchBox
          placeholder={searchPlaceholder}
          searchExpression={searchTerm}
          onChange={onChange}
          onClear={() => onChange('')}
          onSearch={() => {}}
          isFocused
          inputDisabled={disabled}
          searchDisabled={disabled}
          clearDisabled={disabled}
        />
      </InputValidationView>
    </div>
  );
};

AssetsPickerSearchBox.propTypes = {
  /**
   * The handler for updating the search expression
   */
  onSearchChange: PropTypes.func.isRequired,
  /**
   * Whether or not the search box should be disabled
   */
  disabled: PropTypes.bool
};

export default AssetsPickerSearchBox;
